import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs from "../components/shared/Breadcrumbs";
import { toast } from "sonner";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { format } from "date-fns";
import { CalendarIcon, Check, Loader2, Phone, Clock, MapPin } from "lucide-react";

const BookSurveyPage = () => {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quote");
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    preferred_date: null,
    preferred_time: "",
    notes: ""
  });

  const fetchQuote = useCallback(async () => {
    try {
      const response = await api.get(`/quotes/${quoteId}`);
      setQuote(response.data);
    } catch {
      toast.error("Failed to load quote details");
    }
  }, [quoteId]);

  useEffect(() => {
    if (quoteId) {
      fetchQuote();
    }
  }, [quoteId, fetchQuote]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quoteId || !formData.preferred_date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      await api.post("/surveys", {
        quote_id: quoteId,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd"),
        preferred_time: formData.preferred_time,
        notes: formData.notes
      });
      
      setSubmitted(true);
      toast.success("Survey request submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit survey request");
    } finally {
      setLoading(false);
    }
  };

  const timeSlots = [
    "09:00 - 11:00",
    "11:00 - 13:00",
    "13:00 - 15:00",
    "15:00 - 17:00"
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <main className="pt-20">
          <div className="container-max px-4 sm:px-6 lg:px-8 py-20">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-[#16A34A]/10 mx-auto flex items-center justify-center mb-6">
                <Check className="w-10 h-10 text-[#16A34A]" />
              </div>
              <h1 className="text-3xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Survey Request Submitted
              </h1>
              <p className="text-slate-600 mb-8">
                Thank you! We've received your survey request. Our team will contact you 
                within 24 hours to confirm your appointment.
              </p>
              <div className="bg-[#F8F9FA] p-6 text-left space-y-3">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-5 h-5 text-[#FF5A1F]" />
                  <span>Preferred date: {formData.preferred_date && format(formData.preferred_date, "PPP")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#FF5A1F]" />
                  <span>Preferred time: {formData.preferred_time}</span>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Book a Free Home Survey | Hitech Transit Cardiff"
        description="Book a free, no-obligation home survey with Hitech Transit. Our surveyor visits your property to provide an accurate, fixed-price removal quote."
        canonical="/book-survey"
      />
      <Header />
      
      <main className="pt-20">
        <Breadcrumbs items={[{ label: "Book Survey", href: "/book-survey" }]} />
        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Free Survey
              </span>
              <h1 className="text-4xl md:text-5xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Book Your Free Survey
              </h1>
              <p className="text-slate-300 text-lg">
                For larger or more complex moves, our surveyor will visit your property to provide 
                an accurate, detailed quote at no cost to you.
              </p>
            </div>
          </div>
        </section>

        <section className="section-padding">
          <div className="container-max">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form */}
              <div>
                <div className="bg-white border border-slate-200 p-8">
                  <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-6">
                    Request Survey Appointment
                  </h2>

                  {quote && (
                    <div className="bg-[#F8F9FA] p-4 mb-6">
                      <div className="text-sm text-slate-500 mb-1">Quote Reference</div>
                      <div className="font-semibold text-[#0F172A]">{quote.quote_id?.slice(0, 8).toUpperCase()}</div>
                      <div className="text-sm text-slate-600 mt-2">
                        {quote.from_postcode} → {quote.to_postcode}
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label className="label-text">Preferred Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-start text-left font-normal h-12 border-slate-200"
                            data-testid="survey-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.preferred_date ? format(formData.preferred_date, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.preferred_date}
                            onSelect={(date) => setFormData(prev => ({ ...prev, preferred_date: date }))}
                            disabled={(date) => date < new Date() || date.getDay() === 0}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div>
                      <Label className="label-text">Preferred Time Slot *</Label>
                      <Select 
                        value={formData.preferred_time} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, preferred_time: v }))}
                      >
                        <SelectTrigger className="h-12" data-testid="survey-time-select">
                          <SelectValue placeholder="Select time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="label-text">Additional Notes</Label>
                      <Textarea
                        placeholder="Any specific requirements or access instructions..."
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        className="min-h-[120px]"
                        data-testid="survey-notes"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={loading || !quoteId}
                      className="w-full btn-primary h-14 text-lg"
                      data-testid="submit-survey-btn"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Request Survey"}
                    </Button>

                    {!quoteId && (
                      <p className="text-sm text-slate-500 text-center">
                        Please complete the instant quote form first to request a survey.
                      </p>
                    )}
                  </form>
                </div>
              </div>

              {/* Info */}
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                    What to Expect
                  </h3>
                  <div className="space-y-4">
                    {[
                      { title: "Quick Visit", desc: "Our survey typically takes 30-45 minutes" },
                      { title: "No Obligation", desc: "Receive your detailed quote with no commitment" },
                      { title: "Expert Advice", desc: "Get tips on preparing for your move" },
                      { title: "Accurate Pricing", desc: "Final quote based on actual inventory" }
                    ].map((item, i) => (
                      <div key={`star-${i}`} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="font-semibold text-[#0F172A]">{item.title}</div>
                          <div className="text-sm text-slate-600">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-[#F8F9FA] p-6">
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-4">
                    Need Help?
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Call us to discuss your move or to book a survey over the phone.
                  </p>
                  <a 
                    href="tel:07346209593" 
                    className="flex items-center gap-2 text-[#FF5A1F] font-semibold"
                  >
                    <Phone className="w-5 h-5" />
                    07346 209593
                  </a>
                </div>

                <div className="bg-[#0F172A] p-6">
                  <h3 className="text-lg font-['Outfit'] font-bold text-white mb-4">
                    Survey Coverage Areas
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {["Cardiff", "Newport", "Swansea", "London", "Bristol", "Birmingham"].map((area) => (
                      <span 
                        key={area} 
                        className="inline-flex items-center gap-1 px-3 py-1 bg-white/10 text-white text-sm"
                      >
                        <MapPin className="w-3 h-3" /> {area}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BookSurveyPage;
